import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Edit3, Trash2, Users, XCircle, AlertCircle, Save, Settings, Copy, FileText } from "lucide-react";
import { toast } from "sonner";
import {
    fetchLeadStatuses,
    createLeadStatus,
    updateLeadStatus,
    deleteLeadStatus
} from "../../services/crmServices/leadStatusService";
import { getDuplicateCheckSetting, updateDuplicateCheckSetting } from "../../services/crmServices/settingService";
import { useAuth } from "../../hooks/useAuth";
import { TemplatesManagementModal } from "../../components/leads/TemplatesManagementModal";

export function LeadStatusManagement() {
    const { user } = useAuth();
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    const [editingStatus, setEditingStatus] = useState(null);
    const [duplicateCheckEnabled, setDuplicateCheckEnabled] = useState(true);
    const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
    const [formData, setFormData] = useState({
        label: "",
        value: "",
        color: "#3B82F6"
    });

    useEffect(() => {
        if (user.position.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
            return;
        }
        loadLeadStatuses();
        loadDuplicateCheckSetting();
    }, [user.position]);

    const loadDuplicateCheckSetting = async () => {
        try {
            const response = await getDuplicateCheckSetting();
            // response.data = { duplicateCheckEnabled: true|false }
            if (response?.data && response.data.duplicateCheckEnabled !== undefined) {
                setDuplicateCheckEnabled(response.data.duplicateCheckEnabled === true);
            }
        } catch (error) {
            console.error("Error loading duplicate check setting:", error);
        }
    };

    const handleToggleDuplicateCheck = async (enabled) => {
        setDuplicateCheckLoading(true);
        try {
            const response = await updateDuplicateCheckSetting(enabled);
            if (response?.status === 200) {
                setDuplicateCheckEnabled(enabled);
                toast.success(`Duplicate check ${enabled ? "enabled" : "disabled"} successfully`);
            } else {
                toast.error("Failed to update duplicate check setting");
            }
        } catch (error) {
            console.error("Error updating duplicate check setting:", error);
            toast.error("Failed to update duplicate check setting");
        } finally {
            setDuplicateCheckLoading(false);
        }
    };

    const loadLeadStatuses = async () => {
        setLoading(true);
        try {
            const response = await fetchLeadStatuses();
            if (response?.data) {
                setLeadStatuses(response.data);
            }
        } catch (error) {
            console.error("Error loading lead statuses:", error);
            toast.error("Failed to load lead statuses");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.label.trim() || !formData.value.trim()) {
            toast.error("Label and value are required");
            return;
        }

        // Check for duplicate labels
        const isDuplicateLabel = leadStatuses.some(
            (status) =>
                status.label.toLowerCase() === formData.label.toLowerCase() &&
                (!editingStatus || status._id !== editingStatus._id)
        );

        if (isDuplicateLabel) {
            toast.error("Status label already exists");
            return;
        }

        // Check for duplicate values
        const isDuplicateValue = leadStatuses.some(
            (status) =>
                status.value.toLowerCase() === formData.value.toLowerCase() &&
                (!editingStatus || status._id !== editingStatus._id)
        );

        if (isDuplicateValue) {
            toast.error("Status value already exists");
            return;
        }

        setLoading(true);
        try {
            let response;
            if (editingStatus) {
                response = await updateLeadStatus({ ...formData, _id: editingStatus._id });
            } else {
                response = await createLeadStatus(formData);
            }

            if (response?.status === 200) {
                toast.success(`Lead status ${editingStatus ? "updated" : "created"} successfully`);
                loadLeadStatuses();
                handleCloseModal();
            } else {
                toast.error(`Failed to ${editingStatus ? "update" : "create"} lead status`);
            }
        } catch (error) {
            console.error("Error saving lead status:", error);
            toast.error(`Failed to ${editingStatus ? "update" : "create"} lead status`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (status) => {
        setEditingStatus(status);
        setFormData({
            label: status.label,
            value: status.value,
            color: status.color || "#3B82F6"
        });
        setShowCreateModal(true);
    };

    const handleDelete = async (statusId) => {
        if (window.confirm("Are you sure you want to delete this status? This action cannot be undone.")) {
            setLoading(true);
            try {
                const response = await deleteLeadStatus(statusId);
                if (response?.status === 200) {
                    toast.success("Lead status deleted successfully");
                    loadLeadStatuses();
                } else {
                    toast.error("Failed to delete lead status");
                }
            } catch (error) {
                console.error("Error deleting lead status:", error);
                toast.error("Failed to delete lead status");
            } finally {
                setLoading(false);
            }
        }
    };

    // Add this function to generate value from label
    const generateValueFromLabel = (label) => {
        return label
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s]/g, "") // Remove special characters
            .replace(/\s+/g, "_") // Replace spaces with underscores
            .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
    };

    // Add this function to generate colors
    const generateRandomColor = () => {
        const colors = [
            "#3B82F6", // Blue
            "#10B981", // Green
            "#F59E0B", // Yellow
            "#EF4444", // Red
            "#8B5CF6", // Purple
            "#F97316", // Orange
            "#06B6D4", // Cyan
            "#84CC16", // Lime
            "#EC4899", // Pink
            "#6366F1", // Indigo
            "#14B8A6", // Teal
            "#F472B6", // Rose
            "#A855F7", // Violet
            "#22C55E", // Emerald
            "#F97A9B", // Hot Pink
            "#FBBF24" // Amber
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
        setEditingStatus(null);
        setFormData({
            label: "",
            value: "",
            color: generateRandomColor() // Generate new random color when opening modal
        });
    };

    if (user.position.role !== "admin") {
        return (
            <div className="w-full max-w-md mx-auto px-4 mt-8">
                <div className="text-center py-8 bg-white border border-red-200 rounded">
                    <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">Access Denied</h3>
                    <p className="text-xs text-gray-600">Administrator privileges required.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[1600px] mx-auto px-4">
            {/* Compact Header */}
            <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">Lead Status Management</h1>
                        <p className="text-xs text-gray-600 mt-0.5">Configure lead statuses</p>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span><span className="font-semibold text-blue-600">{leadStatuses.length}</span> total</span>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors ml-4"
                        >
                            <Plus className="h-3.5 w-3.5 mr-1.5" />
                            Add Status
                        </button>
                        <button
                            onClick={() => setShowTemplatesModal(true)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-300 hover:bg-purple-100 rounded transition-colors"
                        >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            Templates
                        </button>
                    </div>
                </div>

                {/* Duplicate Check Setting */}
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded mb-3">
                    <div className="flex items-center gap-2.5">
                        <Copy className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <div>
                            <p className="text-xs font-semibold text-gray-800">Duplicate Check</p>
                            <p className="text-[11px] text-gray-500">
                                {duplicateCheckEnabled
                                    ? "Leads with duplicate phone numbers are skipped during CSV import"
                                    : "All leads are imported regardless of duplicate phone numbers"}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-medium ${duplicateCheckEnabled ? "text-green-600" : "text-gray-400"}`}>
                            {duplicateCheckEnabled ? "Enabled" : "Disabled"}
                        </span>
                        <Switch
                            checked={duplicateCheckEnabled}
                            onCheckedChange={handleToggleDuplicateCheck}
                            disabled={duplicateCheckLoading}
                            className="scale-90"
                        />
                    </div>
                </div>

            </div>

            {/* Compact Table */}
            <div className="rounded border border-gray-200 overflow-x-auto bg-white">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50">
                                <TableHead className="text-xs py-2">Status</TableHead>
                                <TableHead className="text-xs py-2">Value</TableHead>
                                <TableHead className="text-xs py-2">Color</TableHead>
                                <TableHead className="text-xs py-2">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody className="text-xs">
                            {leadStatuses.map((status) => (
                                <TableRow key={status._id}>
                                    <TableCell className="py-2">
                                        <Badge style={{ backgroundColor: status.color }} className="text-white text-[10px] px-1.5 py-0.5">
                                            {status.label}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="font-mono py-2">{status.value}</TableCell>
                                    <TableCell className="py-2">
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="w-3.5 h-3.5 rounded border border-gray-300"
                                                style={{ backgroundColor: status.color }}
                                            />
                                            <span className="text-[11px] text-gray-600">{status.color}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="py-2">
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleEdit(status)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded transition-colors"
                                            >
                                                <Edit3 className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(status._id)}
                                                className="inline-flex items-center justify-center h-7 w-7 text-red-600 bg-white border border-red-300 hover:bg-red-50:bg-red-900/30 rounded transition-colors"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {leadStatuses.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center py-8">
                                        <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-700">No lead statuses found</p>
                                        <p className="text-xs text-gray-500">Create your first status to get started</p>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

            <TemplatesManagementModal
                open={showTemplatesModal}
                onClose={() => setShowTemplatesModal(false)}
            />

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal} onOpenChange={handleCloseModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{editingStatus ? "Edit Lead Status" : "Create Lead Status"}</DialogTitle>
                        <DialogDescription>
                            {editingStatus ? "Update the lead status details" : "Add a new lead status to the system"}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="label">Status Label *</Label>
                                <Input
                                    id="label"
                                    value={formData.label}
                                    onChange={(e) => {
                                        const newLabel = e.target.value;
                                        setFormData((prev) => ({
                                            ...prev,
                                            label: newLabel,
                                            // Auto-generate value from label (only if not editing or value is empty/auto-generated)
                                            value: !editingStatus ? generateValueFromLabel(newLabel) : prev.value
                                        }));
                                    }}
                                    placeholder="e.g., Interested"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="value">Status Value *</Label>
                                <Input
                                    id="value"
                                    value={formData.value}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            value: e.target.value.toLowerCase().replace(/\s+/g, "-")
                                        }))
                                    }
                                    placeholder="Auto-generated from label"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Auto-generated from label. You can modify it manually.
                                </p>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="color">Color</Label>
                            <div className="flex items-center space-x-2">
                                <Input
                                    id="color"
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                                    className="w-16 h-10"
                                />
                                <Input
                                    value={formData.color}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                                    placeholder="#3B82F6"
                                    className="flex-1"
                                />
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setFormData((prev) => ({ ...prev, color: generateRandomColor() }))}
                                >
                                    Random
                                </Button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Random color generated automatically. Click &quot;Random&quot; for a new color.
                            </p>
                        </div>

                        <div className="space-y-3">
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button type="button" variant="outline" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={loading}>
                                <Save className="h-4 w-4 mr-2" />
                                {editingStatus ? "Update" : "Create"} Status
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
